import React, { forwardRef, useEffect, useMemo, useRef, useState } from 'react';
import { Bubble, Button, Dropdown, Input, List } from 'tea-component';
import { connect } from 'umi';
import { ISingleCommonType, TOptionsItem } from '../../types';
import { singleMapStateToProps, singleMergeProps } from '../../connect/single';
import useIntls from '@opm/hooks/useIntls';
import './index.scss';
import { useUpdateEffect } from 'ahooks';
import { CustomRangeSelectButton, renderOptionItem } from './CustomRangeSelectButton';
import { addFilterBuriedPoint } from '../../utils/point';
import { AllFilterPointType } from '@opm/operationLog/pages/filter';

// 自定义输入面板组件
interface CustomInputPanelProps {
  unit?: string;
  placeholder?: string;
  onConfirm: (value: {
    start: number;
    end: number;
    displayValue: string;
  }) => void;
  onBack: () => void;
}

function CustomInputPanel({
  unit,
  placeholder,
  onConfirm,
  onBack,
}: CustomInputPanelProps) {
  const { intl } = useIntls();
  const regexText = /^\d+(\.\d{1,3})?$/;

  const [startRange, setStartRange] = useState('');
  const [endRange, setEndRange] = useState('');
  const [inputStartErrorText, setInputStartErrorText] = useState<string | null>(
    null,
  );
  const [inputEndErrorText, setInputEndErrorText] = useState<string | null>(
    null,
  );

  const getErrorMessage = (isSimple = true) => {
    let startErrorText = '';
    let endErrorText = '';
    const empty = 'Please enter numbers, symbols are not supported.';
    const valueError =
      'Please enter a numerical value to three decimal places.';

    if (startRange !== '' && !regexText.test(startRange)) {
      startErrorText = valueError;
    }
    if (endRange !== '' && !regexText.test(endRange)) {
      endErrorText = valueError;
    }

    if (!isSimple) {
      const start = Number(startRange);
      const end = Number(endRange);
      if (start < 0 || isNaN(start) || end < 0 || isNaN(end)) {
        startErrorText = empty;
        endErrorText = empty;
      }
    }

    return {
      startErrorText,
      endErrorText,
      isFalse: !!startErrorText || !!endErrorText,
    };
  };

  const changeInputState = (isSimple = true) => {
    const { startErrorText, endErrorText, isFalse } = getErrorMessage(isSimple);
    setInputStartErrorText(startErrorText);
    setInputEndErrorText(endErrorText);
    return isFalse;
  };

  useEffect(() => {
    changeInputState();
  }, [startRange, endRange]);

  const btnDisabled = useMemo(() => {
    const { isFalse } = getErrorMessage(false);
    return isFalse;
  }, [startRange, endRange]);

  const getStartAndEndValue = (
    startRange: number | string,
    endRange: number | string,
  ) => {
    const s = typeof startRange === 'string' ? Number(startRange) : startRange;
    const e = typeof endRange === 'string' ? Number(endRange) : endRange;
    if (s > e) return [e, s];
    return [s, e];
  };

  const handleConfirm = () => {
    if (changeInputState(false)) return;
    const [start, end] = getStartAndEndValue(startRange, endRange);
    const displayValue = `${start}${unit || ''} ~ ${end}${unit || ''}`;
    onConfirm({ start, end, displayValue });
    // 重置状态
    setStartRange('');
    setEndRange('');
    setInputStartErrorText(null);
    setInputEndErrorText(null);
  };

  return (
    <div className="dbom-range-filter-input-wrap">
      <div className="dbom-range-filter-input">
        <div
          className={`dbom-range-filter-input-item ${
            inputStartErrorText ? 'error' : ''
          }`}
        >
          <Bubble
            error
            content={inputStartErrorText || ''}
            visible={!!inputStartErrorText}
          >
            <Input
              value={startRange}
              onChange={(value) => {
                setStartRange(value);
              }}
              placeholder={placeholder || ''}
            />
          </Bubble>
          {unit && <div className="dbom-range-filter-input-unit">{unit}</div>}
        </div>

        <span className="dbom-range-filter-input-gap">-</span>
        <div
          className={`dbom-range-filter-input-item ${
            inputEndErrorText ? 'error' : ''
          }`}
        >
          <Bubble
            error
            content={inputEndErrorText || ''}
            visible={!!inputEndErrorText}
          >
            <Input
              value={endRange}
              onChange={(value) => {
                setEndRange(value);
              }}
              placeholder={placeholder || ''}
            />
          </Bubble>
          {unit && <div className="dbom-range-filter-input-unit">{unit}</div>}
        </div>
      </div>
      <div className="dbom-range-filter-btngroup">
        <Button disabled={btnDisabled} type="primary" onClick={handleConfirm}>
          {intl({ id: 'Confirm' })}
        </Button>
        <Button type="weak" onClick={onBack}>
          {intl({ id: 'Back' })}
        </Button>
      </div>
    </div>
  );
}

export type TCustomRangeSelectType = {
  options: TOptionsItem[];
  itemRender?: (item: TOptionsItem, isEn: boolean) => string | JSX.Element;
  valueKeys: string;
  title: string;
  titleCn: string;
  filterClassName?: string;
  hasCustomize?: boolean;
  unit?: string;
  placeholder?: string;
  instanceId?: string;
  /** 当 value 不在 options 中时，添加新选项的回调 */
  addOption?: (value: string, unit?: string) => TOptionsItem | void;
  pointKey?: AllFilterPointType;
};

export function CustomRangeSelect({
  options,
  itemRender,
  change,
  value,
  title,
  titleCn,
  filterClassName,
  hasCustomize = true,
  unit = 'K',
  placeholder,
  addOption,
  pointKey,
}: TCustomRangeSelectType & ISingleCommonType) {
  const { intl, isEn } = useIntls();

  const optionsRef = useRef<TOptionsItem[]>([...options]);

  // 面板类型 select/customized
  const [activePanel, setActivePanel] = useState('select');
  // 动态新增自定义option
  const [rangeOptions, setRangeOptions] = useState<TOptionsItem[]>([
    ...options,
  ]);

  useUpdateEffect(() => {
    optionsRef.current = [...options];
    setRangeOptions([...options]);
  }, [options]);

  // 初始化时检查 value 是否在 options 中，如果不在则调用 addOption
  useEffect(() => {
    if (value && addOption) {
      const isValueInOptions = options.some((opt) => opt.value === value);
      if (!isValueInOptions) {
        const newOption = addOption(value, unit);
        if (newOption) {
          optionsRef.current = [...optionsRef.current, newOption];
          setRangeOptions((prev) => [...prev, newOption]);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleCustomConfirm = ({
    start,
    end,
    displayValue,
  }: {
    start: number;
    end: number;
    displayValue: string;
  }) => {
    const newRange: TOptionsItem = {
      text: displayValue,
      textCn: displayValue,
      value: `${start}-${end}`,
    };
    // 动态新增自定义
    optionsRef.current = [...optionsRef.current, newRange];
    setRangeOptions([...rangeOptions, newRange]);
    change(newRange.value);
    // 触发过滤器埋点
    addFilterBuriedPoint(pointKey, newRange.value);
    // 切换下拉面板
    setActivePanel('select');
  };

  const handleSelectChange = (selectedValue: string, close: () => void) => {
    change(selectedValue);
    // 触发过滤器埋点
    addFilterBuriedPoint(pointKey, selectedValue);
    close();
  };

  return (
    <div className={`dbom-custom-range-select-wrapper ${filterClassName}`}>
      <Dropdown
        className="dbom-range-filter"
        button={
          <CustomRangeSelectButton
            value={value}
            options={rangeOptions}
            title={title}
            titleCn={titleCn}
            itemRender={itemRender}
            addOption={addOption}
            unit={unit}
          />
        }
        size="m"
        appearance="button"
        matchButtonWidth
        clickClose={false}
      >
        {(close) => (
          <>
            {activePanel === 'select' ? (
              <div className="dbom-range-filter-select-wrap">
                <List type="option" className="dbom-range-filter-selectbox">
                  {rangeOptions?.map((item, index) => (
                    <List.Item
                      className={value === item.value ? 'is-selected' : ''}
                      key={index}
                      onClick={() => handleSelectChange(item.value, close)}
                    >
                      {renderOptionItem(item, isEn, itemRender)}
                    </List.Item>
                  ))}
                </List>
                {hasCustomize && (
                  <div
                    className="dbom-range-filter-footer"
                    onClick={() => {
                      setActivePanel('customized');
                    }}
                  >
                    {intl({ id: 'Customize' })}
                  </div>
                )}
              </div>
            ) : (
              <CustomInputPanel
                unit={unit}
                placeholder={placeholder}
                onConfirm={handleCustomConfirm}
                onBack={() => setActivePanel('select')}
              />
            )}
          </>
        )}
      </Dropdown>
    </div>
  );
}

export default connect(
  singleMapStateToProps,
  singleMergeProps,
)(forwardRef(CustomRangeSelect));
